// FE-SCRM-054 — Recent-searches localStorage helpers.
//
// Persists the last five rows the producer has selected from the
// global-search modal. Surfaces in the modal when the input is
// empty so a return visit lands on "the prospect I was looking at
// five minutes ago" instead of a blank slate.

import type { SearchEntityType, SearchResultRow } from "./types";


export const RECENT_SEARCHES_KEY = "benecrm.salesCrm.recentSearches.v1";
const MAX_ENTRIES = 5;


export interface RecentSearchEntry extends SearchResultRow {
  entity_type: SearchEntityType;
}


function _isEntry(x: unknown): x is RecentSearchEntry {
  if (typeof x !== "object" || x === null) return false;
  const r = x as Record<string, unknown>;
  return (
    typeof r.entity_type === "string"
    && typeof r.id === "string"
    && typeof r.label === "string"
    && typeof r.route === "string"
    && (r.sublabel === null || typeof r.sublabel === "string")
  );
}


export function loadRecentSearches(): RecentSearchEntry[] {
  try {
    const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(_isEntry).slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
}


export function addRecentSearch(entry: RecentSearchEntry): void {
  const current = loadRecentSearches();
  const filtered = current.filter(
    (r) => !(r.entity_type === entry.entity_type && r.id === entry.id),
  );
  const next = [entry, ...filtered].slice(0, MAX_ENTRIES);
  try {
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch {
    // localStorage may be unavailable (private-mode Safari, quota,
    // ...). The modal still works without persistence; we just lose
    // the recent-searches affordance.
  }
}


export function clearRecentSearches(): void {
  try {
    window.localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // See addRecentSearch — best-effort, swallow.
  }
}
